import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { isFuture } from 'date-fns';
import type { ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { usePrevious } from '@sorare/core/src/hooks/usePrevious';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  ItemImgContainer,
  ItemInfosContainer,
} from '#src/components/ItemPreview/ui.tsx';
import { LevelUpAnimation } from '#src/components/card/levelup/LevelUpAnimation/index.tsx';
import { TokenImg } from '#src/components/token/Token/TokenImg/index.tsx';

import type { TokenRoot_anyCard } from './__generated__/index.graphql.ts';

const Root = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: min-content 1fr;
  gap: var(--intermediate-unit);
  &.isDesktopLayout {
    display: flex;
    flex-direction: column;
  }
`;

const ActionWrapper = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
  display: none;

  @media ${tabletAndAbove} {
    &.forceShow {
      display: block;
    }
    ${ItemImgContainer}:hover & {
      display: block;
    }
  }
`;

const StyledItemImgContainer = styled(ItemImgContainer)`
  & > *:first-child {
    transition: opacity 0.2s ease-in-out;
  }

  &.hasAction {
    @media ${tabletAndAbove} {
      &:hover {
        & > *:first-child {
          opacity: 0.5;
        }
      }
    }
  }
`;

type Props = {
  anyCard: TokenRoot_anyCard;
  hideDetails: boolean;
  action?: ReactNode;
  children: ReactNode;
  galleryOwnerSlug?: string;
  forceShowActionButton?: boolean;
} & Pick<
  ComponentProps<typeof TokenImg>,
  | 'onToggleCard'
  | 'selected'
  | 'selectionMode'
  | 'stackedTokensCount'
  | 'totalXpNeeded'
>;

export const TokenRoot = ({
  anyCard,
  stackedTokensCount,
  hideDetails,
  action,
  onToggleCard,
  selected,
  selectionMode,
  children,
  totalXpNeeded,
  galleryOwnerSlug,
  forceShowActionButton,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const [levelUpAnimate, setLevelUpAnimate] = useState(false);

  const isDesktopLayout = true;

  const isBundledAuction = !!(
    anyCard.latestEnglishAuction &&
    isFuture(anyCard.latestEnglishAuction.endDate) &&
    anyCard.latestEnglishAuction?.cards?.length > 1
  );

  const isMyGallery = galleryOwnerSlug === currentUser?.slug;

  const previousGrade = usePrevious(anyCard.grade);
  if (
    !levelUpAnimate &&
    typeof previousGrade === 'number' &&
    previousGrade !== anyCard.grade
  ) {
    setLevelUpAnimate(true);
  }

  return (
    <Root className={classNames({ isDesktopLayout })}>
      <StyledItemImgContainer
        className={classNames({
          isDesktopLayout,
          hasAction: !!action,
        })}
      >
        <ConditionalWrapper
          wrap={isMyGallery}
          Wrapper={LevelUpAnimation}
          props={{ animate: levelUpAnimate }}
        >
          <TokenImg
            isDesktopLayout
            card={anyCard}
            stackedTokensCount={stackedTokensCount}
            isBundledAuction={isBundledAuction}
            onToggleCard={onToggleCard}
            selected={selected}
            selectionMode={selectionMode}
            totalXpNeeded={totalXpNeeded}
          />
        </ConditionalWrapper>
        {!!action && (
          <ActionWrapper
            className={classNames({ forceShow: forceShowActionButton })}
          >
            {action}
          </ActionWrapper>
        )}
      </StyledItemImgContainer>
      {!hideDetails && (
        <ItemInfosContainer className={classNames({ isDesktopLayout })}>
          {children}
        </ItemInfosContainer>
      )}
    </Root>
  );
};

TokenRoot.fragments = {
  anyCard: gql`
    fragment TokenRoot_anyCard on AnyCardInterface {
      slug
      grade
      latestEnglishAuction {
        id
        endDate
        cards: anyCards {
          slug
        }
      }
      ...TokenImg_anyCard
    }
    ${TokenImg.fragments.anyCard}
  ` as TypedDocumentNode<TokenRoot_anyCard>,
};
